'use strict';
(() => {
  function createLookup({ fetchProduct, origin, root = () => '/', now = () => Date.now(), schedule = setTimeout, cancel = clearTimeout, timeout = 8000 }) {
    const cache = new Map(), pending = new Map(), queue = [];
    let active = 0;
    const transient = reason => ({ status: 'transient', reason });
    async function request(job) {
      const controller = new AbortController();
      let timer;
      const timedOut = new Promise(resolve => { timer = schedule(() => { controller.abort(); resolve(transient('timeout')); }, timeout); });
      const fetched = (async () => {
        try {
          const response = await fetchProduct(job.url, { method: 'GET', credentials: 'same-origin', mode: 'same-origin', redirect: 'error', cache: 'no-store', headers: { Accept: 'application/json' }, signal: controller.signal });
          if (response.status === 404 || response.status === 410) return { status: 'missing', httpStatus: response.status, reason: 'not-found' };
          if (!response.ok) return { ...transient('http-error'), httpStatus: response.status };
          const product = await response.json();
          if (!Number.isSafeInteger(product?.id) || product.id <= 0 || typeof product.handle !== 'string' || typeof product.title !== 'string' || !product.title || !Array.isArray(product.variants) || !product.variants.length || product.variants.some(variant => !Number.isSafeInteger(variant.id) || variant.id <= 0 || !Number.isSafeInteger(variant.price) || variant.price < 0)) return transient('invalid-response');
          if (product.handle.toLowerCase() !== job.key) return { status: 'missing', reason: 'identity-changed' };
          return { status: 'ok', product };
        } catch (_) { return transient('network-error'); }
      })();
      try { return await Promise.race([fetched, timedOut]); }
      finally { cancel(timer); }
    }
    function drain() {
      while (active < 2 && queue.length) {
        const job = queue.shift();
        active += 1;
        void request(job).then(result => {
          pending.delete(job.key);
          active -= 1;
          if (result.status === 'ok' || result.status === 'missing') {
            result.expiresAt = now() + (result.status === 'ok' ? 45000 : 30000);
            cache.set(job.key, { promise: job.promise, expiresAt: result.expiresAt });
            if (cache.size > 100) cache.delete(cache.keys().next().value);
          }
          job.resolve(result);
          drain();
        });
      }
    }
    function read(handle) {
      if (typeof handle !== 'string' || !/^[a-z0-9][a-z0-9_-]{0,254}$/i.test(handle)) return Promise.resolve(transient('invalid-handle'));
      const key = handle.toLowerCase();
      const existing = cache.get(key);
      if (existing && existing.expiresAt > now()) return existing.promise;
      if (existing) cache.delete(key);
      if (pending.has(key)) return pending.get(key);
      let url;
      try {
        const base = typeof origin === 'function' ? origin() : origin;
        const prefix = root();
        if (typeof prefix !== 'string' || !prefix.endsWith('/')) return Promise.resolve(transient('invalid-route'));
        url = new URL(prefix + 'products/' + encodeURIComponent(key) + '.js', base);
        if (url.origin !== new URL(base).origin || !['http:', 'https:'].includes(url.protocol)) return Promise.resolve(transient('invalid-route'));
      } catch (_) { return Promise.resolve(transient('invalid-route')); }
      let resolve;
      const promise = new Promise(done => { resolve = done; });
      pending.set(key, promise);
      queue.push({ key, url: url.href, promise, resolve });
      drain();
      return promise;
    }
    return Object.freeze({ read });
  }
  if (typeof module !== 'undefined') module.exports = { createLookup };
  if (typeof window !== 'undefined' && !window.OluvieProductLookup) window.OluvieProductLookup = createLookup({ fetchProduct: (...args) => window.fetch(...args), origin: () => window.location.origin, root: () => window.Shopify?.routes?.root || '/' });
})();
